import type { PlanFeatureFlagKey } from "../plan/catalog.js";

/**
 * Registro único de módulos del panel del comercio. La API (middlewares de permiso y
 * de módulo activable), la web (menú, guards de ruta, Ctrl+K, pantalla de permisos) y
 * el ERP (activación por tenant) derivan de acá. Agregar un módulo = sumar sus entradas
 * en este archivo; no hay otra lista que mantener.
 *
 * Tres capas, en AND (ver docs/analisis/patron-modulos-desacoplados.md):
 * 1. Plan: `ACTIVATABLE_MODULES[k].flag` en `Plan.features` (lo comercial).
 * 2. Tenant: `TenantModule.enabled` (activación operativa desde el ERP).
 * 3. Usuario: área de permiso (`User.permisos[area] === true`, o rol admin).
 */

/** Áreas con permiso por usuario (`User.permisos[area]`), en el orden de la pantalla de permisos. */
export const PERMISSION_AREAS = {
  dashboard: { label: "Inicio" },
  ventas: { label: "Ventas" },
  compras: { label: "Compras" },
  inventario: { label: "Inventario" },
  servicios: { label: "Servicios" },
  contratos: { label: "Contratos" },
  clientes: { label: "Clientes" },
  proveedores: { label: "Proveedores" },
  presupuestos: { label: "Presupuestos" },
  reportes: { label: "Reportes" },
  caja: { label: "Caja" },
  config: { label: "Configuración del negocio" },
  gastronomia: { label: "Gastronomía" },
  rrhh: { label: "Empleados (RRHH)" },
} as const satisfies Record<string, { label: string }>;

export type PermissionArea = keyof typeof PERMISSION_AREAS;
export const PERMISSION_AREA_IDS = Object.keys(PERMISSION_AREAS) as PermissionArea[];

/** Módulos que se activan por plan + tenant. */
export const ACTIVATABLE_MODULES = {
  gastronomia: {
    flag: "gastronomia",
    label: "Gastronomía",
    description: "Pedidos por mesa, menú y visor de cocina.",
  },
  facturacion_electronica: {
    flag: "facturacion_electronica",
    label: "Facturación electrónica",
    description: "Emisión de documentos tributarios electrónicos vía SIFEN.",
  },
  servicios: {
    flag: "servicios",
    label: "Servicios",
    description: "Catálogo y venta de servicios sin control de stock.",
  },
  contratos: {
    flag: "contratos",
    label: "Contratos y cuotas",
    description: "Contratos con cuotas y cobros programados.",
  },
  balanza: {
    flag: "balanza",
    label: "Balanza",
    description: "Integración con balanzas electrónicas (códigos EAN-13 embebidos).",
  },
  cuentas: {
    flag: "cuentas",
    label: "Cuentas",
    description: "Cuentas de cobro/pago y conciliación de movimientos.",
  },
  rrhh: {
    flag: "rrhh",
    label: "Recursos humanos",
    description: "Legajo de empleados, con vínculo opcional a usuarios del sistema.",
  },
} as const satisfies Record<string, { flag: PlanFeatureFlagKey; label: string; description: string }>;

export type ActivatableModuleKey = keyof typeof ACTIVATABLE_MODULES;
export const ACTIVATABLE_MODULE_KEYS = Object.keys(ACTIVATABLE_MODULES) as ActivatableModuleKey[];
export type ModuleFlags = Record<ActivatableModuleKey, boolean>;

type NavAccessDef = {
  /** Alcanza con tener permiso en cualquiera de estas áreas. */
  areas: readonly PermissionArea[];
  /** Módulo activable que la pantalla necesita (plan + tenant). */
  module?: ActivatableModuleKey;
  /** Solo admin/superadmin del tenant, aunque el usuario tenga el área. */
  adminOnly?: true;
};

/** Pantallas del panel (códigos de navegación) → quién puede verlas. */
export const NAV_ACCESS = {
  HOME: { areas: ["dashboard"] },
  INTELIGENCIA_NEGOCIO: { areas: ["dashboard", "reportes"] },
  VENTAS: { areas: ["ventas"] },
  VER_VENTAS: { areas: ["ventas"] },
  DEVOLUCIONES: { areas: ["ventas"] },
  CUENTAS_POR_COBRAR: { areas: ["ventas"] },
  PRESUPUESTO: { areas: ["presupuestos"] },
  CAJA: { areas: ["caja"] },
  REPORTE_VENTAS: { areas: ["reportes"] },
  ACTIVIDAD_NEGOCIO: { areas: ["reportes"] },
  COMPRAS: { areas: ["compras"] },
  VER_COMPRAS: { areas: ["compras"] },
  NOTA_PEDIDO: { areas: ["compras"] },
  CUENTAS_POR_PAGAR: { areas: ["compras"] },
  INVENTARIO: { areas: ["inventario"] },
  INVENTARIO_TAXONOMIA: { areas: ["inventario"] },
  AJUSTES_INVENTARIO: { areas: ["inventario"] },
  CLIENTES: { areas: ["clientes"] },
  PROVEEDORES: { areas: ["proveedores"] },
  SERVICIOS: { areas: ["servicios"], module: "servicios" },
  CONTRATOS: { areas: ["contratos"], module: "contratos" },
  GASTRONOMIA: { areas: ["gastronomia"], module: "gastronomia" },
  CUENTAS: { areas: ["config"], module: "cuentas" },
  FACTURACION_ELECTRONICA: { areas: ["config"], module: "facturacion_electronica", adminOnly: true },
  EMPLEADOS: { areas: ["rrhh"], module: "rrhh" },
  PRECIOS: { areas: ["config"], adminOnly: true },
  CONFIG: { areas: ["config"], adminOnly: true },
} as const satisfies Record<string, NavAccessDef>;

export type NavCode = keyof typeof NAV_ACCESS;

/** Lo mínimo del usuario que necesitan las reglas de acceso (sirve para el User de Prisma y el AuthUser de la web). */
export type AccessUser = {
  rol?: string | null;
  permisos?: unknown;
};

export function isTenantAdmin(user: AccessUser | null | undefined): boolean {
  return user?.rol === "admin" || user?.rol === "superadmin";
}

function permisoTrue(user: AccessUser, key: string): boolean {
  const permisos = user.permisos;
  return !!permisos && typeof permisos === "object" && (permisos as Record<string, unknown>)[key] === true;
}

/** ¿El usuario tiene permiso en el área? Admin siempre; si no, `permisos[area] === true`. */
export function hasAreaAccess(user: AccessUser | null | undefined, area: string): boolean {
  if (!user) return false;
  if (isTenantAdmin(user)) return true;
  const a = String(area || "")
    .trim()
    .toLowerCase();
  if (!a) return false;
  return permisoTrue(user, a);
}

/**
 * ¿El usuario puede abrir la pantalla `code`? No mira si el módulo activable está
 * habilitado para el tenant: eso lo resuelven los flags (`ModuleFlags`).
 * Un código desconocido se trata como área con el mismo nombre en minúsculas.
 */
export function canAccessNavCode(user: AccessUser | null | undefined, code: string): boolean {
  if (!user) return false;
  const def = (NAV_ACCESS as Record<string, NavAccessDef>)[code];
  if (def?.adminOnly && !isTenantAdmin(user)) return false;
  if (isTenantAdmin(user)) return true;
  const areas = def ? def.areas : [String(code).toLowerCase()];
  return areas.some((a) => hasAreaAccess(user, a));
}

/** Módulo activable que necesita una pantalla (o `undefined` si no depende de ninguno). */
export function navCodeModule(code: string): ActivatableModuleKey | undefined {
  return (NAV_ACCESS as Record<string, NavAccessDef>)[code]?.module;
}

/**
 * Situación comercial de un módulo activable para un tenant (GET /api/modules/offer):
 * - `active`: habilitado (plan + tenant).
 * - `activable`: el plan lo incluye pero el tenant no lo activó (se activa en Mi suscripción).
 * - `upgrade`: el plan actual no lo incluye; `plan` es el plan más económico que sí.
 * - `unavailable`: ningún plan público lo ofrece.
 */
export type ModuleOfferState = "active" | "activable" | "upgrade" | "unavailable";
export type ModuleOffer = { state: ModuleOfferState; plan?: { id: string; name: string } };
export type ModuleOffers = Record<ActivatableModuleKey, ModuleOffer>;
